import { useState, useEffect } from 'react';
import { Calendar, CalendarDays, MapPin, Plus, Edit, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { API, authFetch } from './helpers';

const CalendarTab = () => {
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: '', description: '', location: '', start_date: '', end_date: '',
    start_time: '', end_time: '', is_public: true, is_all_day: true, event_type: 'event'
  });

  const fetchEvents = async () => {
    const res = await authFetch(`${API}/calendar-events/all`);
    if (res.ok) setEvents(await res.json());
  };

  useEffect(() => { fetchEvents(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ title: '', description: '', location: '', start_date: '', end_date: '', start_time: '', end_time: '', is_public: true, is_all_day: true, event_type: 'event' });
    setShowForm(true);
  };

  const openEdit = (ev) => {
    setEditing(ev);
    setForm({
      title: ev.title || '', description: ev.description || '', location: ev.location || '',
      start_date: ev.start_date || '', end_date: ev.end_date || '', start_time: ev.start_time || '',
      end_time: ev.end_time || '', is_public: ev.is_public !== false, is_all_day: ev.is_all_day !== false,
      event_type: ev.event_type || 'event'
    });
    setShowForm(true);
  };

  const saveEvent = async (e) => {
    e.preventDefault();
    if (editing) {
      await authFetch(`${API}/calendar-events/${editing.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    } else {
      await authFetch(`${API}/calendar-events`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    }
    setShowForm(false); setEditing(null); fetchEvents();
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    await authFetch(`${API}/calendar-events/${id}`, { method: 'DELETE' });
    fetchEvents();
  };

  return (
    <div data-testid="calendar-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-brand-dark">Events Calendar</h2>
        <button onClick={openNew} className="flex items-center gap-2 bg-brand-gold px-4 py-2 text-sm font-medium text-brand-dark hover:opacity-90" data-testid="create-event-btn">
          <Plus className="w-4 h-4" /> New Event
        </button>
      </div>

      {showForm && (
        <div className="bg-white p-6 mb-6" data-testid="event-form">
          <h3 className="text-lg font-medium text-brand-dark mb-4">{editing ? 'Edit Event' : 'Create New Event'}</h3>
          <form onSubmit={saveEvent} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label className="text-brand-dark font-medium">Event Title</Label><Input required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="mt-1" placeholder="e.g., Men's Mental Health Reset" data-testid="event-title-input" /></div>
              <div>
                <Label className="text-brand-dark font-medium">Event Type</Label>
                <select value={form.event_type} onChange={(e) => setForm({...form, event_type: e.target.value})} className="mt-1 w-full border border-gray-300 p-2 rounded" data-testid="event-type-select">
                  <option value="event">Event</option><option value="workshop">Workshop</option><option value="meeting">Meeting</option><option value="fundraiser">Fundraiser</option><option value="retreat">Retreat</option>
                </select>
              </div>
            </div>
            <div><Label className="text-brand-dark font-medium">Location</Label><Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} className="mt-1" placeholder="Venue, City" data-testid="event-location-input" /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label className="text-brand-dark font-medium">Start Date</Label><Input type="date" required value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} className="mt-1" data-testid="event-start-date" /></div>
              <div><Label className="text-brand-dark font-medium">End Date (optional)</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})} className="mt-1" data-testid="event-end-date" /></div>
            </div>
            <div className="flex items-center gap-3 mb-2">
              <input type="checkbox" id="allDay" checked={form.is_all_day} onChange={(e) => setForm({...form, is_all_day: e.target.checked})} className="w-4 h-4" data-testid="event-all-day-check" />
              <Label htmlFor="allDay" className="text-brand-dark">All-day event</Label>
            </div>
            {!form.is_all_day && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label className="text-brand-dark font-medium">Start Time</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({...form, start_time: e.target.value})} className="mt-1" data-testid="event-start-time" /></div>
                <div><Label className="text-brand-dark font-medium">End Time</Label><Input type="time" value={form.end_time} onChange={(e) => setForm({...form, end_time: e.target.value})} className="mt-1" data-testid="event-end-time" /></div>
              </div>
            )}
            <div><Label className="text-brand-dark font-medium">Description</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-1" rows={3} data-testid="event-description-input" /></div>
            <div className="flex items-center gap-3">
              <input type="checkbox" id="isPublic" checked={form.is_public} onChange={(e) => setForm({...form, is_public: e.target.checked})} className="w-4 h-4" data-testid="event-public-check" />
              <Label htmlFor="isPublic" className="text-brand-dark">Show on public calendar</Label>
            </div>
            <div className="flex gap-4">
              <button type="submit" className="bg-brand-gold px-6 py-2 text-sm font-medium text-brand-dark hover:opacity-90" data-testid="save-event-btn">{editing ? 'Update Event' : 'Create Event'}</button>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="bg-gray-200 px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {events.length === 0 ? (
        <div className="text-center py-12 bg-white"><CalendarDays className="w-12 h-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">No events yet. Create your first event above.</p></div>
      ) : (
        <div className="space-y-3" data-testid="admin-events-list">
          {events.map((ev) => (
            <div key={ev.id} className="bg-white p-5" data-testid={`admin-event-${ev.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="font-medium text-brand-dark truncate">{ev.title}</h3>
                    <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 flex-shrink-0">{ev.event_type}</span>
                    {!ev.is_public && <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 flex-shrink-0">Private</span>}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{ev.start_date}{ev.end_date && ev.end_date !== ev.start_date && ` to ${ev.end_date}`}</span>
                    {ev.start_time && <span>{ev.start_time}{ev.end_time && ` - ${ev.end_time}`}</span>}
                    {ev.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {ev.location}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-4 flex-shrink-0">
                  <button onClick={() => openEdit(ev)} className="p-2 text-gray-400 hover:text-brand-dark" data-testid={`edit-event-${ev.id}`}><Edit className="w-4 h-4" /></button>
                  <button onClick={() => deleteEvent(ev.id)} className="p-2 text-gray-400 hover:text-red-600" data-testid={`delete-event-${ev.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CalendarTab;
